import { MessageType } from '../types/messageTypes';

// Base interface for all message data
export interface BaseMessageData {
    type: MessageType;
    prompt_id: string;
    semantic_model: string;
    account_name: string;
    showFeedback?: boolean;
    [key: string]: any; // For additional properties
}

// Specific message data interfaces
export interface UserMessageData extends BaseMessageData {
    type: MessageType.USER;
    text: string;
}

export interface BotMessageData extends BaseMessageData {
    type: MessageType.BOT;
    text: string;
}

export interface CompleteMessageData extends BaseMessageData {
    type: MessageType.COMPLETE;
    text: React.ReactNode;
}

export interface SuggestionsMessageData extends BaseMessageData {
    type: MessageType.SUGGESTIONS;
    suggestions: string[];
}

export interface SQLQueryMessageData extends BaseMessageData {
    type: MessageType.SQL_QUERY;
    sql: string;
    columns?: any;
    data?: any;
}

export interface SQLResultMessageData extends BaseMessageData {
    type: MessageType.SQL_RESULT;
    sql: string;
    columns: any;
    data: any;
}

export interface TmpQueryMessageData extends BaseMessageData {
    type: MessageType.TMP_QUERY;
    sql: string;
}

export interface PreChatMessageData extends BaseMessageData {
    type: MessageType.PRE_CHAT;
    text: string;
    id: string;
}

// Union type for all message data
export type MessageData =
    | UserMessageData
    | BotMessageData
    | CompleteMessageData
    | SuggestionsMessageData
    | SQLQueryMessageData
    | SQLResultMessageData
    | TmpQueryMessageData
    | PreChatMessageData;

// MessageBuilder class
export class MessageBuilder {
    private message: Partial<BaseMessageData> = {};

    constructor(
        private promptId: string,
        private semanticModel: string,
        private accountName: string
    ) { }

    // Set message type
    setType(type: MessageType): this {
        this.message.type = type;
        return this;
    }

    // Set text content
    setText(text: string | React.ReactNode): this {
        this.message.text = text;
        return this;
    }

    // Set suggestions
    setSuggestions(suggestions: string[]): this {
        this.message.suggestions = suggestions;
        return this;
    }

    // Set SQL content
    setSQL(sql: string): this {
        this.message.sql = sql;
        return this;
    }

    // Set SQL result data
    setSQLResult(columns: any, data: any): this {
        this.message.columns = columns;
        this.message.data = data;
        return this;
    }

    // Set custom ID (for pre_chat messages)
    setId(id: string): this {
        this.message.id = id;
        return this;
    }

    // Set feedback flag
    setShowFeedback(show: boolean = true): this {
        this.message.showFeedback = show;
        return this;
    }

    // Set additional custom properties
    setCustom(key: string, value: any): this {
        this.message[key] = value;
        return this;
    }

    // Build the final message
    build(): MessageData {
        // Add required fields
        const finalMessage = {
            ...this.message,
            prompt_id: this.promptId,
            semantic_model: this.semanticModel,
            account_name: this.accountName,
        } as MessageData;

        // Validate required fields based on type
        this.validateMessage(finalMessage);

        return finalMessage;
    }

    // Validate message based on type
    private validateMessage(message: MessageData): void {
        switch (message.type) {
            case MessageType.USER:
            case MessageType.BOT:
            case MessageType.COMPLETE:
                if (!message.text) {
                    throw new Error(`${message.type} message requires text property`);
                }
                break;

            case MessageType.PRE_CHAT:
                if (!message.text) {
                    throw new Error(`${message.type} message requires text property`);
                }
                if (!message.id) {
                    throw new Error(`${message.type} message requires id property`);
                }
                break;

            case MessageType.SUGGESTIONS:
                if (!message.suggestions || !Array.isArray(message.suggestions)) {
                    throw new Error(`${message.type} message requires suggestions array`);
                }
                break;

            case MessageType.SQL_QUERY:
            case MessageType.SQL_RESULT:
            case MessageType.TMP_QUERY:
                if (!message.sql) {
                    throw new Error(`${message.type} message requires sql property`);
                }
                break;
        }
    }

    // Convenience methods for common message types
    static user(promptId: string, semanticModel: string, accountName: string, text: string): MessageData {
        return new MessageBuilder(promptId, semanticModel, accountName)
            .setType(MessageType.USER)
            .setText(text)
            .build();
    }

    static bot(promptId: string, semanticModel: string, accountName: string, text: string, showFeedback: boolean = true): MessageData {
        return new MessageBuilder(promptId, semanticModel, accountName)
            .setType(MessageType.BOT)
            .setText(text)
            .setShowFeedback(showFeedback)
            .build();
    }

    static complete(promptId: string, semanticModel: string, accountName: string, text: React.ReactNode): MessageData {
        return new MessageBuilder(promptId, semanticModel, accountName)
            .setType(MessageType.COMPLETE)
            .setText(text)
            .setShowFeedback(true)
            .build();
    }

    static suggestions(promptId: string, semanticModel: string, accountName: string, suggestions: string[]): MessageData {
        return new MessageBuilder(promptId, semanticModel, accountName)
            .setType(MessageType.SUGGESTIONS)
            .setSuggestions(suggestions)
            .setShowFeedback(true)
            .build();
    }

    static sqlQuery(promptId: string, semanticModel: string, accountName: string, sql: string): MessageData {
        return new MessageBuilder(promptId, semanticModel, accountName)
            .setType(MessageType.SQL_QUERY)
            .setSQL(sql)
            .setShowFeedback(true)
            .build();
    }

    static sqlResult(promptId: string, semanticModel: string, accountName: string, sql: string, columns: any, data: any): MessageData {
        return new MessageBuilder(promptId, semanticModel, accountName)
            .setType(MessageType.SQL_RESULT)
            .setSQL(sql)
            .setSQLResult(columns, data)
            .setShowFeedback(true)
            .build();
    }

    static tmpQuery(promptId: string, semanticModel: string, accountName: string, sql: string): MessageData {
        return new MessageBuilder(promptId, semanticModel, accountName)
            .setType(MessageType.TMP_QUERY)
            .setSQL(sql)
            .setShowFeedback(true)
            .build();
    }

    static preChat(promptId: string, semanticModel: string, accountName: string, text: string, id: string): MessageData {
        return new MessageBuilder(promptId, semanticModel, accountName)
            .setType(MessageType.PRE_CHAT)
            .setText(text)
            .setId(id)
            .build();
    }
}
